import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from '@appchoose/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@appchoose/form';
import { toast } from '@appchoose/toast';
import { useQueryClient } from '@tanstack/react-query';

import { TextareaAutosize } from '../../components/textarea-autosize/textarea-autosize';
import { useCreateSupplierBillingAdjustmentNoteMutation } from '../../types/generated-new';

type FinancialAdjustmentNoteForm = {
  content: string;
};

type FinancialAdjustmentNoteFormProps = {
  supplierBillingAdjustmentId: string;
};

export const FinancialAdjustmentNoteForm: React.FC<
  FinancialAdjustmentNoteFormProps
> = ({ supplierBillingAdjustmentId }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutateAsync: createSupplierBillingAdjustmentNote } =
    useCreateSupplierBillingAdjustmentNoteMutation();

  const form = useForm<FinancialAdjustmentNoteForm>({
    mode: 'onTouched',
  });

  const onSubmit = async (data: FinancialAdjustmentNoteForm) => {
    try {
      await createSupplierBillingAdjustmentNote({
        supplierBillingAdjustmentId,
        content: data.content,
      });

      await queryClient.invalidateQueries({
        queryKey: [
          'supplierBillingAdjustment',
          { supplierBillingAdjustmentId },
        ],
      });

      form.reset({
        content: '',
      });
    } catch {
      toast.error(t('invoice.financial_adjustment.note.add_note_error'));
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="content"
          rules={{
            required: true,
          }}
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2 space-y-0">
              <FormLabel className="sr-only">
                {t('invoice.financial_adjustment.note.fields.content.label')}
              </FormLabel>
              <FormControl>
                <TextareaAutosize
                  className="w-full resize-none"
                  minRows={1}
                  {...field}
                  placeholder={t(
                    'invoice.financial_adjustment.note.fields.content.placeholder'
                  )}
                />
              </FormControl>
              <FormMessage match="required">
                {t(
                  'invoice.financial_adjustment.note.fields.content.validation_errors.required'
                )}
              </FormMessage>
            </FormItem>
          )}
        />
        <div className="flex justify-end">
          <Button type="submit">
            {t('invoice.financial_adjustment.note.add')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
